export const MAX_EXTERNAL_AGENT_SEARCH_SEED = 0xffffffff;

const EXTERNAL_AGENT_SEARCH_SEED_PATTERN = /^(0|[1-9]\d{0,9})$/;
const EXTERNAL_AGENT_SEARCH_SEED_STORAGE_KEY = 'appx.externalAgents.searchSeed';
let inMemoryExternalAgentSearchSeed: number | undefined;

/** Accept only values the recommendation service can use as unsigned 32-bit seeds. */
export const isValidExternalAgentSearchSeed = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_EXTERNAL_AGENT_SEARCH_SEED;

/** Parse persisted seed state without accepting coercible or malformed values. */
export const parseExternalAgentSearchSeed = (value: unknown): number | undefined => {
    if (typeof value !== 'string' || !EXTERNAL_AGENT_SEARCH_SEED_PATTERN.test(value)) {
        return undefined;
    }

    const seed = Number(value);
    return isValidExternalAgentSearchSeed(seed) ? seed : undefined;
};

/** Generate the identity for a new keywordless External Agent result order. */
export const generateExternalAgentSearchSeed = (): number => {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0];
};

/**
 * Keep one ordering identity for this browser tab. Session storage preserves
 * filters, pagination, Back navigation, and refresh without exposing the
 * implementation detail in the public search URL.
 */
export const getOrCreateExternalAgentSearchSeed = (): number => {
    try {
        const storedSeed = parseExternalAgentSearchSeed(
            globalThis.window?.sessionStorage.getItem(EXTERNAL_AGENT_SEARCH_SEED_STORAGE_KEY),
        );
        if (storedSeed !== undefined) {
            return storedSeed;
        }
        if (inMemoryExternalAgentSearchSeed !== undefined) {
            return inMemoryExternalAgentSearchSeed;
        }
    } catch {
        // Restricted storage should not prevent stable in-page pagination.
        inMemoryExternalAgentSearchSeed ??= generateExternalAgentSearchSeed();
        return inMemoryExternalAgentSearchSeed;
    }

    const seed = generateExternalAgentSearchSeed();
    try {
        globalThis.window?.sessionStorage.setItem(EXTERNAL_AGENT_SEARCH_SEED_STORAGE_KEY, String(seed));
    } catch {
        inMemoryExternalAgentSearchSeed = seed;
    }
    return seed;
};
